// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import moment from 'moment'

import {
  Employees,
  FilterSidebar,
  FilterEmployerGroupEmployers,
  FilterEmploymentType,
  FilterEmploymentStatus,
  FilterEmployerPayrollCycle,
  FilterEmployerDivision,
  FilterDateRange,
  FilterPicklistWithData,
  FilterMessageChannel
} from '../../components'

import { AccountContext } from '../../context'


const getDefaultState = (pathname) => (
  {
    search: '',
    employerFilter: '',
    employmentTypeFilter: '',
    employmentStatusFilter: 'active',
    payrollCycleFilter: '',
    payrollCycleCycleFilter: '',
    channelFilter: '',
    divisionFilter: '',
    createdAtStartDateFilter: null,
    createdAtEndDateFilter: null
  }
)

const getFiltersFromQueryString = (querySearchString) => {
  let queryParams = qs.parse(querySearchString)

  let filters = Object.assign(
    {},
    ...Object.keys(queryParams).map(key => (
      ['createdAtStartDate', 'createdAtEndDate'].indexOf(key) > -1
        ? { [key]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
        : { [`${key}Filter`]: queryParams[key] }
    ))
  )

  return filters
}

// NOTE: uncomment if we want number records in employee listing to be responsive to screen size
// const getRecordsPerPage = () => {
//   // the amount of employee records to show in listing is determined by screen height (pixels)
//   if (window.screen.availHeight <= 777) {
//     return 10
//   }
//
//   if (window.screen.availHeight <= 877) {
//     return 12
//   }
//
//   return 15
// }


class EmployeeList extends Component {
  static propTypes = {
    selectableRows: PropTypes.bool,
    onSelectionChange: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    selectableRows: false,
    className: ''
  }

  state = this.props.location.search
    ? Object.assign(
        getDefaultState(this.props.match.path),
        getFiltersFromQueryString(this.props.location.search)
      )
    : getDefaultState(this.props.match.path)

  UNSAFE_componentWillMount() {
    if (this.props.location.search) {
      // if initially url was entered, or copy-and-pasted, with filters specified in url search params, set state accordingly
      this.setState(getFiltersFromQueryString())
    }
     else {
      // if initially, page was opened via global nav, set default url search params according to current initial state filter variables
      this.setQueryString(true)
    }
  }

  setQueryString = (setDefault, identityType) => {
    const state = this.state
    let filters = {
      // orderBy: state.orderBy
    }

    if (state.search) {
      filters.search = state.search
    }

    if (!!state.employerFilter) {
      filters.employerFilter = state.employerFilter
    }

    if (!!state.employmentTypeFilter) {
      filters.employmentType = state.employmentTypeFilter
    }

    if (setDefault) {
      filters.employmentStatus = 'active'
    } else if (!!state.employmentStatusFilter) {
      filters.employmentStatus = state.employmentStatusFilter
    }

    if (!!state.payrollCycleFilter) {
      filters.payrollCycle = state.payrollCycleFilter
    }

    if (!!state.payrollCycleCycleFilter) {
      filters.payrollCycleCycle = state.payrollCycleCycleFilter
    }

    if (!!state.channelFilter) {
      filters.channel = state.channelFilter
    }

    if (!!state.divisionFilter) {
      filters.division = state.divisionFilter
    }

    if (state.createdAtStartDate) {
      filters.createdAtStartDate = state.createdAtStartDateFilter.format('YYYYMMDDTHHmm')
    }
    if (state.createdAtEndDate) {
      filters.createdAtEndDate = state.createdAtEndDateFilter.format('YYYYMMDDTHHmm')
    }

    this.props.history.push({ search: qs.stringify(filters) })
  }

  getFilters = (accountId, isEmployerGroupContact) => {
    let filters = [
      <FilterEmploymentType
        value={this.state.employmentTypeFilter}
        onChange={employmentType => (
          this.setState(
            {
              employmentTypeFilter: employmentType === 'all' ? '' : employmentType
            },
            this.setQueryString
          )
        )}
        key='employmentTypeFilter'
      />,
      <FilterEmploymentStatus
        value={this.state.employmentStatusFilter}
        onChange={employmentStatus => (
          this.setState(
            {
              employmentStatusFilter: employmentStatus === 'all' ? '' : employmentStatus
            },
            this.setQueryString
          )
        )}
        key='employmentStatusFilter'
      />,
      <FilterEmployerDivision
        employerId={isEmployerGroupContact
          ? this.state.employerFilter
          : accountId
        }
        value={this.state.divisionFilter}
        onChange={division => (
          this.setState(
            {
              divisionFilter: division === 'all' ? '' : division
            },
            this.setQueryString
          )
        )}
        key='divisionFilter'
      />,
      <Fragment>
        {!!this.state.employerFilter
          ? <FilterEmployerPayrollCycle
              employerId={this.state.employerFilter}
              payrollCycleId={this.state.payrollCycleFilter}
              onChange={payrollCycleId => (
                this.setState(
                  {
                    payrollCycleFilter: payrollCycleId === 'All'
                      ? ''
                      : payrollCycleId
                  },
                  this.setQueryString
                )
              )}
              showEmployerSelector={false}
              key='payrollCycleFilter'
            />
          : <FilterPicklistWithData
              title='Payroll cycle'
              forField='PayrollCycleCycle'
              value={this.state.payrollCycleCycleFilter}
              onChange={payrollCycleCycle => (
                this.setState(
                  {
                    payrollCycleCycleFilter: payrollCycleCycle === 'All'
                      ? ''
                      : payrollCycleCycle
                  },
                  this.setQueryString
                )
              )}
            />
        }
      </Fragment>,
      <FilterMessageChannel
        title='Channel'
        value={this.state.channelFilter}
        onChange={channel => (
          this.setState(
            {
              channelFilter: channel === 'All' ? '' : channel
            },
            this.setQueryString
          )
        )}
        key='messageChannelFilter'
      />,
      <FilterDateRange
        title='Created between'
        startDate={this.state.createdAtStartDateFilter}
        endDate={this.state.createdAtEndDateFilter}
        onStartDateChange={date => (
          this.setState({ createdAtStartDateFilter: date }, this.setQueryString)
        )}
        onEndDateChange={date => (
          this.setState({ createdAtEndDateFilter: date }, this.setQueryString)
        )}
        showTimeSelect={true}
        showYearDropdown={true}
        showMonthDropdown={true}
        key='createdBetweenFilter'
      />
    ]

    if (isEmployerGroupContact) {
      filters.unshift(
        <FilterEmployerGroupEmployers
          employerGroupId={accountId}
          value={this.state.employerFilter}
          onChange={employer => {
            if (employer === 'all') {
              this.setState(
                {
                  employerFilter: '',
                  payrollCycleFilter: '',
                  divisionFilter: ''
                },
                this.setQueryString
              )
            } else {
              this.setState(
                {
                  employerFilter: employer,
                  payrollCycleCycleFilter: ''
                },
                this.setQueryString
              )
            }
          }}
          key='employerFilter'
        />
      )
    }

    return filters
  }

  render() {
    const { employerFilter, employmentTypeFilter, employmentStatusFilter, payrollCycleFilter, payrollCycleCycleFilter, channelFilter, divisionFilter, createdAtStartDateFilter, createdAtEndDateFilter, search } = this.state

    return (
      <AccountContext.Consumer>
        {({ currentAccountId, isEmployerGroupAccount }) => (
          <div className={`slds-grid ${this.props.className}`} style={{ height: 'calc(100vh - 80px)' }}>
            <FilterSidebar
              filters={this.getFilters(currentAccountId, isEmployerGroupAccount)}
              componentFilters={true}
              filtersApplied={
                [
                  employerFilter,
                  employmentTypeFilter,
                  employmentStatusFilter,
                  payrollCycleFilter,
                  payrollCycleCycleFilter,
                  channelFilter,
                  divisionFilter,
                  createdAtStartDateFilter || createdAtEndDateFilter
                    ? createdAtStartDateFilter
                      ? createdAtStartDateFilter.toString()
                      : createdAtEndDateFilter.toString()
                    : ''
                ].filter(filter => filter.length > 0).length
              }
              onClear={() => (
                this.setState(
                  {
                    employerFilter: '',
                    employmentTypeFilter: '',
                    employmentStatusFilter: '',
                    payrollCycleFilter: '',
                    payrollCycleCycleFilter: '',
                    channelFilter: '',
                    divisionFilter: '',
                    createdAtStartDateFilter: null,
                    createdAtEndDateFilter: null
                  },
                  this.setQueryString
                )
              )}
              search={true}
              searchString={search}
              onSearchClick={search => this.setState({ search })}
              onSearchClear={() => this.setState({ search: '' })}
            />
            {/* <h3>Employees</h3> */}
            <div className='slds-scrollable slds-large-size_5-of-6 slds-medium-size_7-of-12'>
              <Employees
                employerGroupId={isEmployerGroupAccount
                  ? currentAccountId
                  : null
                }
                employerId={isEmployerGroupAccount
                  ? null
                  : currentAccountId
                }
                employmentType={employmentTypeFilter}
                employmentStatus={employmentStatusFilter}
                division={divisionFilter}
                payrollcycle={payrollCycleFilter}
                payrollcycleCycle={payrollCycleCycleFilter}
                channel={channelFilter}
                createdAtStartDate={createdAtStartDateFilter}
                createdAtEndDate={createdAtEndDateFilter}
                search={search}
                selectableRows={this.props.selectableRows}
                onSelectionChange={this.props.onSelectionChange}
              />
            </div>
          </div>
        )}
      </AccountContext.Consumer>
    )
  }
}

export default withRouter(EmployeeList)



// WEBPACK FOOTER //
// ./src/containers/messageplus/EmployeeList.js